import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { domainActions, goalActions } from 'store';
import { Checkbox } from '@mui/material';
import { AccordionFullTable, CreateChancel, FullTable, NoItemsYet, TextRow } from 'components';
import { goalNotYet, goalsBody, goalsHead, goalsWithTargetsTableInfo, ACTION_TYPE } from './constants';
import { useModal } from 'utils';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * With `goalTargets` (`{ [goalId]: [{ id, TARGET_NAME, TARGET_OBJECTIVE, TARGET_STATUS }] }`, Goal
 * Mode tables with Target Mode on) the goals are an accordion, like "Choose from Library": a goal
 * opens its targets to pick from. Checking a target picks its goal, unchecking the last one drops
 * it, and checking a goal picks all of its targets. `saveSelectedIds` then also gets the picked
 * target ids.
 *
 * `goalTypes` (goal type ids) limits the list to those types (a progress report Goal block).
 */
export const GoalSelector = ({ clientId, saveSelectedIds, selectedGoals, onClose, skipClinicalData, goalTargets, selectedTargetIds, goalTypes }) => {
    const dispatch = useDispatch();
    const allGoals = useSelector((state) => state.goals.simpleGoals);
    const goalList = goalTypes?.length && Array.isArray(allGoals) ? allGoals.filter((goal) => goalTypes.includes(goal?.type)) : allGoals;
    const domainList = useSelector((state) => state.domains.domainList);
    const { close: closeModal } = useModal();
    const close = onClose || closeModal;
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const [selectedList, setSelectedList] = useState(selectedGoals || []);
    const [saveParams, setSaveParams] = useState({});
    const [targetIds, setTargetIds] = useState(() => new Set(selectedTargetIds || []));
    const targetsOf = (goalId) => goalTargets?.[goalId] || [];

    useEffect(() => {
        dispatch(domainActions.getDomains());
    }, [dispatch]);

    useEffect(() => {
        const params = {
            ...saveParams,
        };
        dispatch(goalActions.getSimpleGoals(clientId, params));
    }, [saveParams]);

    const handleCheck = (ev, item, name) => {
        ev.stopPropagation();
        ev.preventDefault();

        if (name === 'all') {
            let allList = goalList?.length === selectedList?.length ? [] : goalList;
            setSelectedList(allList);
        } else {
            const list = [...selectedList];
            const index = list.findIndex((selected) => selected.id === item.id);

            if (index === -1) {
                list.push(item);
            } else {
                list.splice(index, 1);
            }
            setSelectedList(list);
        }
    };

    const toggleTargets = (targets, on) =>
        setTargetIds((prev) => {
            const next = new Set(prev);
            targets.forEach((target) => (on ? next.add(target.id) : next.delete(target.id)));
            return next;
        });

    const isGoalPicked = (goal) => selectedList.some((selected) => selected.id === goal?.id);

    const targetCount = (goal) => {
        const targets = targetsOf(goal?.id);
        return { picked: targets.filter((target) => targetIds.has(target.id)).length, total: targets.length };
    };

    const goalState = (goal) => {
        if (!isGoalPicked(goal)) return 'none';
        const { picked, total } = targetCount(goal);
        return picked === total ? 'all' : 'some';
    };

    const toggleGoal = (goal, checked) => {
        setSelectedList((prev) => [...prev.filter((selected) => selected.id !== goal.id), ...(checked ? [goal] : [])]);
        toggleTargets(targetsOf(goal.id), checked);
    };

    const toggleTarget = (goal, target, checked) => {
        const othersPicked = targetsOf(goal.id).some((t) => t.id !== target.id && targetIds.has(t.id));
        toggleTargets([target], checked);
        if (checked && !isGoalPicked(goal)) setSelectedList((prev) => [...prev, goal]);
        if (!checked && !othersPicked) setSelectedList((prev) => prev.filter((selected) => selected.id !== goal.id));
    };

    /** A partly picked goal is completed rather than cleared. */
    const toggleAll = (checked) => (goalList ?? []).filter((goal) => !checked || goalState(goal) !== 'all').forEach((goal) => toggleGoal(goal, checked));

    const renderTargets = (goal) => {
        const targets = targetsOf(goal?.id);
        if (!targets.length) {
            return (
                <div className="targets-tab-details-wrapper">
                    <NoItemsYet text={'No Targets.'} subTitle={'This goal has no targets.'} containerStyles={{ position: 'static' }} height="fit-content" />
                </div>
            );
        }
        return (
            <div className="targets-tab-details-wrapper">
                <div className="targets-tab-details-block">
                    {targets.map((target, index) => (
                        <div className="targets-tab-details-item-row" key={target.id}>
                            <Checkbox
                                checked={targetIds.has(target.id)}
                                onChange={(e) => toggleTarget(goal, target, e.target.checked)}
                                name={'checkBox'}
                                style={{ padding: 16, color: '#0C66E4' }}
                            />
                            <div className="targets-tab-details-item-cell" style={{ width: 'fit-content' }}>
                                {index + 1}
                            </div>
                            <div className="targets-tab-details-item-cell" style={{ width: '25%' }}>
                                <TextRow name={target.TARGET_NAME} />
                            </div>
                            <div className="targets-tab-details-item-cell" style={{ width: '55%' }}>
                                <TextRow name={target.TARGET_OBJECTIVE} />
                            </div>
                            <div className="targets-tab-details-item-cell" style={{ width: '14%' }}>
                                <TextRow name={target.TARGET_STATUS} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleSave = () => {
        if (selectedList?.length) {
            saveSelectedIds(selectedList, goalTargets ? [...targetIds] : undefined);
            close();

            if (!skipClinicalData && info?.startDate && info?.endDate && selectedList?.length) {
                dispatch(
                    goalActions.getGoalClinicalData({
                        startDate: info?.startDate,
                        endDate: info?.endDate,
                        goalIds: selectedList?.map((g) => g.id),
                    })
                );

                dispatch(
                    goalActions.getGoalTargetClinicalData(
                        {
                            startDate: info?.startDate,
                            endDate: info?.endDate,
                            goalIds: selectedList?.map((g) => g.id),
                        },
                        clientId
                    )
                );

                dispatch(
                    goalActions.getGoalClinicalDefaultValues({
                        ids: selectedList?.map((g) => g.id),
                    })
                );
            }
        }
    };

    return (
        <div style={{ width: '1280px', maxWidth: '100%' }}>
            {goalTargets && <h3 className="modal-header-title">Select Goals and Targets</h3>}
            {goalTargets ? (
                <div className="library-targets-tab-wrapper">
                    <AccordionFullTable
                        getParams={setSaveParams}
                        savedParams={saveParams}
                        classNameWrapper="library-targets-tab-accordion-wrapper"
                        renderAccordionDetails={renderTargets}
                        tableInfo={goalsWithTargetsTableInfo({ goals: goalList, domainList, goalState, targetCount, toggleGoal, toggleAll })}
                        list={goalList || []}
                        // margin={'0'}
                        containerStyles={{ height: '60vh', position: 'relative' }}
                    />
                </div>
            ) : (
                <FullTable
                    getParams={setSaveParams}
                    savedParams={saveParams}
                    head={goalsHead(handleCheck, goalList, selectedList, domainList)}
                    body={goalsBody(handleCheck, goalList, selectedList)}
                    loadingType={ACTION_TYPE}
                    list={goalList}
                    notYet={goalNotYet}
                    activeRowId={''}
                    margin={'0'}
                    height={'medium'}
                    selectedList={selectedList?.map((g) => g?.id)}
                />
            )}

            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
                <CreateChancel
                    disabled={selectedList?.length === 0}
                    create={'Add Selected'}
                    chancel={'Cancel'}
                    onClose={close}
                    onCreate={handleSave}
                    cancelButtonWidth="110px"
                    buttonWidth="130px"
                    justifyContent={'flex-end'}
                />
            </div>
        </div>
    );
};
